
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Zap, Crown, ExternalLink, Sparkles } from "lucide-react";
import { isAndroidTWA } from "@/utils/runtimeContext";
import AndroidNeutralNotice from "@/components/legal/AndroidNeutralNotice";
import { useAnalytics } from "@/hooks/useAnalytics";

const PagamentoPage: React.FC = () => {
  const { track } = useAnalytics();
  const location = useLocation();

  useEffect(() => {
    if (isAndroidTWA()) return;
    track("direct_payment_page_view", {
      origin: "pagamento_direto",
      page: "/pagamento",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll automático para a seção de planos quando vier com âncora #pix-planos
  useEffect(() => {
    if (location.hash !== "#pix-planos") return;
    const t = setTimeout(() => {
      document.getElementById("pix-planos")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [location.hash]);

  if (isAndroidTWA()) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <AndroidNeutralNotice />
      </div>
    );
  }

  const plans = [
    {
      title: "Experimente 15 dias",
      price: "27,90",
      period: "/15 dias",
      link: "https://payment-link-v3.ton.com.br/pl_jmE3QdVoaOkNQxyT0qFpPWqpbYzBAxXw",
      icon: <Sparkles className="h-6 w-6" />,
      popular: false,
      badge: "Experimente",
      color: "from-emerald-500 to-teal-600",
      visible: false,
    },
    {
      title: "Plano Mensal",
      price: "44,90",
      period: "/mês",
      link: "https://payment-link-v3.ton.com.br/pl_ZQwzvp2lJo7NmVQHDxSzXV5YLBM6rP0A",
      icon: <Zap className="h-6 w-6" />,
      popular: false,
      color: "from-blue-500 to-blue-600",
      visible: true,
    },
    {
      title: "Plano Trimestral",
      price: "59,90",
      period: "/trimestre",
      link: "https://payment-link-v3.ton.com.br/pl_MOmQDl0pqL34xMDFQOfyRWdVvkN1aYZg",
      icon: <Star className="h-6 w-6" />,
      popular: true,
      color: "from-primary to-purple-600",
      visible: true,
    },
    {
      title: "Plano Anual",
      price: "189,90",
      period: "/ano",
      link: "https://payment-link-v3.ton.com.br/pl_L70p9dexNmVXEJeJTLU3AgoaMrlk5Zb2",
      icon: <Crown className="h-6 w-6" />,
      popular: false,
      color: "from-amber-500 to-orange-600",
      visible: true,
    },
  ];

  const features = [
    "Milhar, Centena, Dezenas",
    "Grupos, Ternos, Duques",
    "Mega-Sena",
    "Lotofácil",
    "Quina",
    "Dupla Sena",
    "Dia de Sorte",
    "E muito mais!",
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Welcome Section */}
      <div className="text-center mb-10">
        <div className="text-5xl mb-4">🤖</div>
        <h1 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
          Olá! Que bom ter você por aqui!
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          Se prepare para ter em suas mãos os palpites mais avançados para o{" "}
          <strong className="text-foreground">Jogo do Bicho</strong> e{" "}
          <strong className="text-foreground">Loterias da Caixa</strong>, tudo com o poder da
          Inteligência Artificial do <span className="text-primary font-bold">Palpite Guru!</span>
        </p>
      </div>

      {/* Registration CTA */}
      <Card className="mb-10 border-green-500/30 bg-green-500/5">
        <CardContent className="p-6 text-center">
          <p className="text-foreground font-medium mb-3">
            📲 <strong>ENVIAR COMPROVANTE APÓS PAGAMENTO PARA O WHATSAPP</strong>
          </p>
          <a
            href="https://wa.me/5573981988263?text=Ol%C3%A1!%20J%C3%A1%20paguei%2C%20segue%20comprovante."
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              track("direct_payment_whatsapp_click", {
                origin: "pagamento_direto",
                method: "whatsapp",
                page: "/pagamento",
              })
            }
          >
            <Button className="bg-green-600 text-white hover:bg-green-700">
              Já paguei, segue comprovante
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </CardContent>
      </Card>

      {/* Plans */}
      <h2 id="pix-planos" className="text-2xl font-bold text-center mb-2 text-foreground scroll-mt-20">
        Planos com IA disponíveis
      </h2>
      <p className="text-center text-muted-foreground mb-8">Pagamento via PIX / Cartão — processado pela Ton 💳</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {plans.filter((plan) => plan.visible).map((plan) => (
          <Card
            key={plan.title}
            className={`relative overflow-hidden transition-transform hover:scale-105 ${
              plan.popular ? "border-primary shadow-lg shadow-primary/20 ring-2 ring-primary" : "border-border"
            }`}
          >
            {plan.popular && (
              <Badge className="absolute top-3 right-3 bg-primary text-primary-foreground">
                Mais Popular
              </Badge>
            )}
            {plan.badge && !plan.popular && (
              <Badge className="absolute top-3 right-3 bg-emerald-500 text-white">
                {plan.badge}
              </Badge>
            )}
            <CardHeader className="text-center pb-2">
              <div
                className={`mx-auto w-14 h-14 rounded-full bg-gradient-to-br ${plan.color} flex items-center justify-center text-white mb-3`}
              >
                {plan.icon}
              </div>
              <CardTitle className="text-lg">{plan.title}</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="mb-4">
                <span className="text-sm text-muted-foreground">R$</span>
                <span className="text-4xl font-extrabold text-foreground">{plan.price}</span>
                <span className="text-muted-foreground">{plan.period}</span>
              </div>
              <a
                href={plan.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                onClick={() => {
                  const meta = {
                    origin: "pagamento_direto",
                    method: "ton",
                    provider: "ton",
                    page: "/pagamento",
                    plan: plan.title,
                  };
                  track("direct_payment_nubank_click", meta);
                  track("checkout_click", meta);
                }}
              >
                <Button
                  className={`w-full text-white ${
                    plan.popular
                      ? "bg-primary hover:bg-primary/90"
                      : "bg-foreground/80 hover:bg-foreground/70"
                  }`}
                  size="lg"
                >
                  Assinar Agora
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* CTA de dúvidas antes de assinar */}
      <div className="text-center mb-8">
        <a
          href="https://wa.me/5573981988263?text=Ol%C3%A1!%20Estou%20na%20p%C3%A1gina%20de%20pagamento%20e%20tenho%20uma%20d%C3%BAvida%20antes%20de%20assinar."
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            track("whatsapp_click", {
              origin: "payment_page",
              intent: "duvida",
              page: "/pagamento",
            })
          }
        >
          <Button variant="outline" className="border-green-500 text-green-700 hover:bg-green-50 font-semibold">
            💬 Tenho dúvidas antes de assinar
          </Button>
        </a>
      </div>

      {/* Aviso jurídico discreto */}
      <p className="text-center text-xs text-muted-foreground italic mb-10 max-w-2xl mx-auto px-4">
        ⚠️ O IA Palpite Guru oferece ferramentas estatísticas e análises estratégicas, sem garantia de premiação.
      </p>

      {/* Features */}
      <Card className="mb-10">
        <CardHeader>
          <CardTitle className="text-center text-xl">
            🚀 Com seu acesso premium, você terá:
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {features.map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500 shrink-0" />
                <span className="text-sm text-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="border-green-500/30 bg-green-500/5">
        <CardContent className="p-6 text-center space-y-3">
          <p className="text-foreground font-medium">
            ✅ Assim que o pagamento for confirmado, nos envie o comprovante com seu{" "}
            <strong>nome completo</strong> e o <strong>e-mail cadastrado</strong> no site.
          </p>
          <p className="text-primary font-bold text-lg">
            🚀 A liberação do seu acesso é IMEDIATA!
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PagamentoPage;
